import { yupResolver } from '@hookform/resolvers/yup';
import { Typography } from '@material-ui/core';
import { AddCircleOutline } from '@mui/icons-material';
import { Box, Grid, IconButton } from '@mui/material';
import classNames from 'classnames';
import { SGSButton, SGSFormInput, SGSFormLayout } from 'components';
import SGSFileUploader from 'components/SGSFileUploader';
import { sendMessageToResponsable } from 'core/services/workStoppage';
import React, { useState } from 'react';
import { FormProvider, useFieldArray, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import * as Yup from 'yup';
import styles from './Dossier.module.scss';

interface FormSendDocumentProps {
    dossier: any;
    setIsModalOpen: any;
}

type DocumentData = {
    filename: string;
    data: string; // This can be Base64 encoded data
};

type ValidationSchema = {
    documents: (DocumentData | null)[];
    objet: string;
    comment: string;
};

const MAX_FILE_SIZE = 2 * 1024 * 1024; // 2MB
const SUPPORTED_FILE_TYPES = ['image/jpeg', 'image/png', 'application/pdf'];

// TODO :: fix problem validation des documents

const FormSendDocument: React.FC<FormSendDocumentProps> = ({ dossier, setIsModalOpen }) => {
    const { t } = useTranslation();
    const requiredMessage = t('yup.validators.required');
    const [isSubmited, setIsSubmited] = useState(false);

    // Validation schema using Yup
    const validationSchema: Yup.SchemaOf<ValidationSchema> = Yup.object().shape({
        objet: Yup.string().required(requiredMessage),
        comment: Yup.string().required(requiredMessage),
        documents: Yup.array()
            .of(
                Yup.lazy((value): Yup.SchemaOf<any> => {
                    // Allow null values in the documents array
                    if (value === null) {
                        return Yup.mixed().nullable(true);
                    }

                    // Apply object schema for non-null values
                    return Yup.object().shape({
                        filename: Yup.string()
                            .nullable()
                            .matches(/\.(pdf|doc|docx|jpeg|jpg|png)$/i, 'Le fichier doit être au format PDF, DOC, DOCX, JPEG, JPG, ou PNG'),

                        data: Yup.string()
                            .nullable()
                            .test('file-size', 'La taille du fichier doit être supérieure à 0 octets et ne doit pas dépasser 2 Mo', (value) => {
                                if (!value) return true; // Skip if empty
                                const base64Content = value.split(',')[1]; // Strip off the base64 header
                                const fileSize = Buffer.byteLength(base64Content, 'base64');
                                return fileSize > 18 && fileSize <= MAX_FILE_SIZE;
                            })
                            .test('file-type', 'Seuls les fichiers PDF, JPEG, ou PNG sont acceptés', (value) => {
                                if (!value) return true; // Skip if empty
                                const fileType = value.split(';')[0].split(':')[1]; // Extract MIME type
                                return SUPPORTED_FILE_TYPES.includes(fileType);
                            }),
                    });
                }) as unknown as Yup.SchemaOf<any>, // Explicitly cast as `unknown` first, then `Yup.SchemaOf<any>`
            )
            .optional(),
    });

    const methods = useForm<ValidationSchema>({
        resolver: yupResolver(validationSchema),
        defaultValues: {
            documents: [{ filename: '', data: '' }],
            comment: '',
            objet: '',
        },
    });

    const {
        handleSubmit,
        control,
        formState: { errors },
        trigger,
        reset,
    } = methods;

    const { fields, append } = useFieldArray({
        control,
        name: 'documents',
    });

    const onSubmit = async (data: ValidationSchema) => {
        setIsSubmited(true);

        const isValid = await trigger();
        if (!isValid) return;
        const payload = { ...data, dossier };
        delete payload.dossier.indemnites;
        await sendMessageToResponsable(payload)
            .catch(() => {})
            .finally(() => {
                reset();
                setIsSubmited(false);
                setIsModalOpen(false);
                toast.success('Message envoyé');
            });
    };

    const handleCancel = () => {
        reset();
        setIsModalOpen(false);
        setIsSubmited(false);
    };

    const handleAddDocument = () => {
        append({ filename: null, data: null });
    };
    console.log(errors);

    return (
        <Box>
            <FormProvider {...methods}>
                <SGSFormLayout onSubmit={handleSubmit(onSubmit)}>
                    <Grid container spacing={2} alignItems="flex-start">
                        <Grid item xs={12}></Grid>
                        <Grid item xs={12}>
                            <Box className={classNames(styles.title, 'mb-2 mt-n5')}>Objet</Box>
                            <SGSFormInput variant="outlined" name="objet" fullWidth />
                            <Box className={classNames(styles.title, 'mb-2 mt-3')}>Message</Box>
                            <SGSFormInput variant="outlined" name="comment" multiline rows={10} fullWidth />
                        </Grid>
                        <Grid item xs={12}>
                            <Box className={'mt-n2'}>
                                {fields.map((field, index) => (
                                    <>
                                        <SGSFileUploader key={index} field={field} index={index.toString()} fieldName={'documents'} />
                                    </>
                                ))}
                            </Box>
                        </Grid>
                        <Grid item xs={12}>
                            <Box className="d-flex justify-content-center mt-n5">
                                <IconButton onClick={handleAddDocument} color="primary">
                                    <AddCircleOutline /> <Typography className={classNames(styles.txtBlack, 'ml-2')}> Ajouter un document</Typography>
                                </IconButton>
                            </Box>
                        </Grid>
                        <Grid item xs={12}>
                            <Box display="flex" justifyContent="flex-end">
                                <SGSButton variant="contained" color="primary" type="button" onClick={handleCancel} className="mr-2">
                                    ANNULER
                                </SGSButton>
                                <SGSButton variant="contained" color="primary" type="submit" loading={isSubmited}>
                                    ENVOYER
                                </SGSButton>
                            </Box>
                        </Grid>
                    </Grid>
                </SGSFormLayout>
            </FormProvider>
        </Box>
    );
};

export default FormSendDocument;
